import { useState, useCallback } from 'react';
import { fetchDgSessions, fetchDgChecks, saveDgSession } from '../data/dg';
import { getPending } from '../data/local/pending';
import { checkParaLinha, linhaParaCheck, dgTemConteudo } from '../utils/dgMapeamento';

/**
 * Hook para persistência das sessões de diagnóstico de gestação (DG30/DG60)
 * nas tabelas normalizadas.
 * Retorna e salva dados no mesmo shape que service.pregnancy_data usava.
 */
export const useDGSession = (laboratoryId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Carrega sessões DG30 e DG60 de um serviço.
   * Retorna null se não existir nenhuma.
   */
  const loadDGData = useCallback(async (serviceId) => {
    if (!serviceId || !laboratoryId) return null;
    setLoading(true);
    setError(null);
    try {
      const [pend30, pend60] = await Promise.all([
        getPending('dg-DG30', serviceId),
        getPending('dg-DG60', serviceId),
      ]);

      /**
       * O servidor é OPCIONAL quando há lançamento offline esperando.
       *
       * Os outros quatro hooks devolvem o pendente antes de tocar a rede; este
       * ia à rede primeiro, e a falha caía no `catch` lá embaixo devolvendo
       * `null` — levando junto o pendente que acabara de ser lido. A aba então
       * reinicializava a partir das transferências da TE, com os resultados em
       * branco, e o salvamento seguinte gravava esse branco por cima do
       * diagnóstico lançado no curral.
       *
       * Sem pendente, a falha continua subindo: aí não há o que mostrar, e
       * fingir que o serviço não tem DG seria pior que dizer que não deu.
       */
      let sessions = null;
      try {
        sessions = await fetchDgSessions(serviceId);
      } catch (err) {
        if (!pend30 && !pend60) throw err;
        console.warn('[dg] sem servidor; seguindo com o que foi lançado offline:', err.message);
      }

      if ((!sessions || sessions.length === 0) && !pend30 && !pend60) return null;

      const result = {};

      for (const sess of (sessions || [])) {
        const key = sess.check_type === 'DG30' ? 'dg30' : 'dg60';

        const checks = await fetchDgChecks(sess.id);

        result[key] = {
          _session_id: sess.id,
          session: {
            date: sess.session_date,
            technician_name: sess.technician_name,
            method: sess.method
          },
          checks: (checks || []).map(linhaParaCheck),
          completed: sess.completed,
          billing_quantity: sess.billing_quantity
        };
      }

      // O lançado offline é mais novo que o do servidor, então vem por cima.
      if (pend30) result.dg30 = pend30;
      if (pend60) result.dg60 = pend60;

      return Object.keys(result).length > 0 ? result : null;
    } catch (err) {
      console.error('useDGSession.loadDGData:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Salva uma sessão DG (dg30 ou dg60) para um serviço.
   * @param {string} serviceId
   * @param {string} type - 'dg30' | 'dg60'
   * @param {Object} dgBlob - { session, checks, completed, billing_quantity }
   */
  const saveDGSession = useCallback(async (serviceId, type, dgBlob) => {
    if (!serviceId || !laboratoryId || !dgBlob) return null;
    setLoading(true);
    setError(null);
    try {
      const checkType = type.toUpperCase(); // 'DG30' | 'DG60'
      const { session, checks = [], completed, billing_quantity } = dgBlob;

      const sessionPayload = {
        service_id: serviceId,
        laboratory_id: laboratoryId,
        check_type: checkType,
        session_date: session?.date || null,
        technician_name: session?.technician_name || null,
        method: session?.method || 'Ultrassom',
        completed: completed || false,
        billing_quantity: parseInt(billing_quantity) || 0
      };

      // Sem `dg_session_id`: preenchido pela operação, aqui ou na fila.
      const checksPayload = checks.map((c, idx) =>
        checkParaLinha(c, { laboratoryId, sortOrder: idx }));

      return await saveDgSession(sessionPayload, laboratoryId, checksPayload, dgBlob);
    } catch (err) {
      console.error('useDGSession.saveDGSession:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Salva ambas as sessões (dg30 e dg60) de uma vez.
   * @param {string} serviceId
   * @param {Object} pregnancyData - { dg30: {...}, dg60: {...} }
   */
  /**
   * Salva DG30 e DG60. Devolve `null` quando alguma falhou.
   *
   * O RETORNO É O CONSERTO PRINCIPAL. Esta função não devolvia nada, e o
   * `null` que `saveDGSession` produz no erro morria aqui dentro. Quem chama
   * (`WorkflowModal`) decide se deu certo testando `result === null`, então
   * uma falha de gravação da prenhez passava por sucesso: o modal fechava
   * dizendo "Workflow atualizado com sucesso!" e o espelho no serviço era
   * gravado sem nada por baixo. Das cinco etapas, era a única cega — e é a
   * etapa de que depende o faturamento por prenhez.
   *
   * SEQUENCIAL, não `Promise.all`: as duas escrevem na MESMA tabela e
   * disputavam o `setLoading` uma da outra, além do lock. É a mesma razão pela
   * qual o modal salva as abas em fila e a sincronização sobe um item por vez.
   *
   * Sessão sem conteúdo não é gravada. Gravar a DG60 vazia enquanto a DG30
   * tinha resultado criava a linha no banco e, pior, APAGAVA os checks que já
   * estivessem lá, porque a gravação troca a lista inteira.
   */
  const saveDGData = useCallback(async (serviceId, pregnancyData) => {
    if (!serviceId || !laboratoryId || !pregnancyData) return null;

    const aGravar = [['dg30', pregnancyData.dg30], ['dg60', pregnancyData.dg60]]
      .filter(([, blob]) => dgTemConteudo(blob));

    if (aGravar.length === 0) return { gravadas: [] };

    const gravadas = [];
    for (const [tipo, blob] of aGravar) {
      const id = await saveDGSession(serviceId, tipo, blob);
      if (id === null) return null;
      gravadas.push(tipo);
    }
    return { gravadas };
  }, [laboratoryId, saveDGSession]);

  return { loading, error, loadDGData, saveDGSession, saveDGData };
};
